import apiService from './apiService';

// =====================================
// JOURNAL TYPES
// =====================================

// Allow any string for journal type to support custom types
export type JournalType = string;

export interface Journal {
  id: string;
  name: string;
  code: string;
  type: JournalType;
  defaultAccountId?: string;
  companyId: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: {
    entries: number;
  };
}

export interface CreateJournalInput {
  name: string;
  code: string;
  type: JournalType;
  defaultAccountId?: string;
  companyId: string;
}

export interface UpdateJournalInput {
  name?: string;
  code?: string;
  type?: JournalType;
  defaultAccountId?: string;
  active?: boolean;
}

export interface JournalFilters {
  type?: JournalType;
  active?: boolean;
  search?: string;
}

export interface JournalStats {
  total: number;
  active: number;
  inactive: number;
  byType: Array<{
    type: string;
    count: number;
  }>;
}

// =====================================
// JOURNAL ENTRY TYPES
// =====================================

export type JournalEntryState = 'DRAFT' | 'POSTED' | 'CANCELLED';

export interface JournalEntryLine {
  id?: string;
  accountId: string;
  name?: string;
  debit: number;
  credit: number;
  partnerId?: string;
  currencyId?: string;
  amountCurrency?: number;
  analyticAccountId?: string;
  departmentId?: string;
  account?: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
  department?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface JournalEntry {
  id: string;
  name: string;
  journalId: string;
  date: Date | string;
  accountingDate?: Date | string;
  ref?: string;
  narration?: string;
  state: JournalEntryState;
  companyId: string;
  createdBy: string;
  postedBy?: string;
  postedAt?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  journal?: Journal;
  lines: JournalEntryLine[];
  // Odoo-imported fields
  chequeNo?: string;
  partnerId?: string;
  invoiceNo?: string;
}

export interface CreateJournalEntryInput {
  // Optional manual override for Entry #; backend auto-generates when blank.
  name?: string;
  journalId: string;
  date: Date | string;
  accountingDate?: Date | string;
  ref?: string;
  narration?: string;
  lines: Omit<JournalEntryLine, 'id' | 'account'>[];
  // Odoo-imported fields (optional for manual entry)
  chequeNo?: string;
  partnerId?: string;
  invoiceNo?: string;
}

export interface UpdateJournalEntryInput {
  name?: string;
  journalId?: string;
  date?: Date | string;
  accountingDate?: Date | string;
  ref?: string;
  narration?: string;
  lines?: Omit<JournalEntryLine, 'id' | 'account'>[];
  // Odoo-imported fields (optional for manual entry)
  chequeNo?: string;
  partnerId?: string;
  invoiceNo?: string;
}

export interface JournalEntryFilters {
  journalId?: string;
  state?: JournalEntryState;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  search?: string;
  partnerId?: string;
}

export interface ReverseJournalEntryInput {
  reversalDate: Date | string;
  narration?: string;
}

export interface JournalEntryStats {
  total: number;
  draft: number;
  posted: number;
  cancelled: number;
}

export interface GeneralLedgerLine {
  id: string;
  entryId: string;
  accountId: string;
  name?: string;
  debit: number;
  credit: number;
  partnerId?: string;
  partnerName?: string;
  currencyId?: string;
  amountCurrency?: number;
  analyticAccountId?: string;
  reconciled: boolean;
  createdAt: string;
  account: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
  entry: {
    id: string;
    name: string;
    date: Date | string;
    // accountingDate is the user-facing "period" date on the journal entry. Set
    // explicitly on vendor bills and manual JEs; on invoice-posting entries
    // invoiceDate carries the same intent. The General Ledger shows
    // invoiceDate || accountingDate || date in a single "Date" column.
    accountingDate?: Date | string | null;
    ref?: string;
    narration?: string;
    // Set on invoice-posting entries (backend enriches via batch lookup).
    invoiceNo?: string | null;
    invoiceDate?: Date | string | null;
    journal: {
      id: string;
      name: string;
      code: string;
      type: string;
    };
  };
}

export interface GeneralLedgerFilters {
  accountId?: string;
  journalId?: string;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  partnerId?: string;
  reconciled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface GeneralLedgerResponse {
  data: GeneralLedgerLine[];
  total: number;
  totalDebit: number;
  totalCredit: number;
  page: number;
  pageSize: number;
  pages: number;
}

// =====================================
// BULK UPLOAD TYPES
// =====================================

export interface BulkUploadResult {
  success: boolean;
  totalRows: number;
  entriesCreated: number;
  entriesSkipped: number;
  errors: Array<{
    entryName: string;
    error: string;
  }>;
  skippedDuplicates: string[];
}

// =====================================
// JOURNAL SERVICE
// =====================================

class JournalService {
  // ============ JOURNAL ENDPOINTS ============

  /**
   * Get all journals with optional filters
   */
  async getJournals(filters?: JournalFilters): Promise<Journal[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await apiService.get<{ data: Journal[] }>(`/accounting/journals?${params.toString()}`);
    return response.data || [];
  }

  /**
   * Get only active journals
   */
  async getActiveJournals(): Promise<Journal[]> {
    const response = await apiService.get<{ data: Journal[] }>('/accounting/journals/active');
    return response.data || [];
  }

  /**
   * Get journals by type
   */
  async getJournalsByType(type: JournalType): Promise<Journal[]> {
    const response = await apiService.get<{ data: Journal[] }>(`/accounting/journals/type/${type}`);
    return response.data || [];
  }

  /**
   * Get journal by ID
   */
  async getJournalById(id: string): Promise<Journal> {
    const response = await apiService.get<{ data: Journal }>(`/accounting/journals/${id}`);
    return response.data;
  }

  /**
   * Get journal by code
   */
  async getJournalByCode(code: string): Promise<Journal> {
    const response = await apiService.get<{ data: Journal }>(`/accounting/journals/code/${code}`);
    return response.data;
  }

  /**
   * Create a new journal
   */
  async createJournal(data: CreateJournalInput): Promise<Journal> {
    const response = await apiService.post<{ data: Journal }>('/accounting/journals', data);
    return response.data;
  }

  /**
   * Update a journal
   */
  async updateJournal(id: string, data: UpdateJournalInput): Promise<Journal> {
    const response = await apiService.put<{ data: Journal }>(`/accounting/journals/${id}`, data);
    return response.data;
  }

  /**
   * Deactivate a journal (soft delete)
   */
  async deactivateJournal(id: string): Promise<Journal> {
    const response = await apiService.patch<{ data: Journal }>(`/accounting/journals/${id}/deactivate`, {});
    return response.data;
  }

  /**
   * Reactivate a journal
   */
  async reactivateJournal(id: string): Promise<Journal> {
    const response = await apiService.patch<{ data: Journal }>(`/accounting/journals/${id}/reactivate`, {});
    return response.data;
  }

  /**
   * Delete a journal (only if no entries)
   */
  async deleteJournal(id: string): Promise<void> {
    await apiService.delete(`/accounting/journals/${id}`);
  }

  /**
   * Get journal statistics
   */
  async getJournalStats(): Promise<JournalStats> {
    const response = await apiService.get<{ data: JournalStats }>('/accounting/journals/stats');
    return response.data;
  }

  // ============ JOURNAL ENTRY ENDPOINTS ============

  /**
   * Get all journal entries with optional filters
   */
  async getJournalEntries(filters?: JournalEntryFilters): Promise<JournalEntry[]> {
    const params = new URLSearchParams();
    if (filters?.journalId) params.append('journalId', filters.journalId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.dateFrom) params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo) params.append('dateTo', new Date(filters.dateTo).toISOString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);

    const response = await apiService.get<{ data: JournalEntry[] }>(`/accounting/journal-entries?${params.toString()}`);
    return response.data || [];
  }

  /**
   * Get journal entry by ID
   */
  async getJournalEntryById(id: string): Promise<JournalEntry> {
    const response = await apiService.get<{ data: JournalEntry }>(`/accounting/journal-entries/${id}`);
    return response.data;
  }

  /**
   * Get journal entry by name (entry number)
   */
  async getJournalEntryByName(name: string): Promise<JournalEntry> {
    const response = await apiService.get<{ data: JournalEntry }>(`/accounting/journal-entries/name/${name}`);
    return response.data;
  }

  /**
   * The number a new entry in this journal would be given, so the form can
   * show it as soon as a journal is picked. Nothing is reserved — the number
   * is worked out again on save.
   */
  async getNextEntryNumber(journalId: string, date?: string): Promise<string> {
    const params = new URLSearchParams({ journalId });
    if (date) params.append('date', date);
    const response = await apiService.get<{ data: { name: string } }>(
      `/accounting/journal-entries/next-number?${params.toString()}`
    );
    return response.data?.name || '';
  }

  /**
   * Create a new journal entry
   */
  async createJournalEntry(data: CreateJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.post<{ data: JournalEntry }>('/accounting/journal-entries', data);
    return response.data;
  }

  /**
   * Update a journal entry (only DRAFT entries)
   */
  async updateJournalEntry(id: string, data: UpdateJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.put<{ data: JournalEntry }>(`/accounting/journal-entries/${id}`, data);
    return response.data;
  }

  /**
   * Post a journal entry (DRAFT → POSTED)
   */
  async postJournalEntry(id: string): Promise<JournalEntry> {
    const response = await apiService.post<{ data: JournalEntry }>(`/accounting/journal-entries/${id}/post`, {});
    return response.data;
  }

  /**
   * Reverse a posted journal entry
   */
  async reverseJournalEntry(id: string, data: ReverseJournalEntryInput): Promise<JournalEntry> {
    const response = await apiService.post<{ data: JournalEntry }>(`/accounting/journal-entries/${id}/reverse`, data);
    return response.data;
  }

  /**
   * Cancel a journal entry
   */
  async cancelJournalEntry(id: string): Promise<JournalEntry> {
    const response = await apiService.patch<{ data: JournalEntry }>(`/accounting/journal-entries/${id}/cancel`, {});
    return response.data;
  }

  /**
   * Delete a journal entry (only DRAFT entries)
   */
  async deleteJournalEntry(id: string): Promise<void> {
    await apiService.delete(`/accounting/journal-entries/${id}`);
  }

  /**
   * Get journal entry statistics
   */
  async getJournalEntryStats(): Promise<JournalEntryStats> {
    const response = await apiService.get<{ data: JournalEntryStats }>('/accounting/journal-entries/stats');
    return response.data;
  }

  // ============ GENERAL LEDGER ENDPOINTS ============

  /**
   * Get general ledger (posted entry lines) with pagination
   */
  async getGeneralLedger(filters?: GeneralLedgerFilters): Promise<GeneralLedgerResponse> {
    const params = new URLSearchParams();
    if (filters?.accountId) params.append('accountId', filters.accountId);
    if (filters?.journalId) params.append('journalId', filters.journalId);
    if (filters?.dateFrom) params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo) params.append('dateTo', new Date(filters.dateTo).toISOString());
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    if (filters?.reconciled !== undefined) params.append('reconciled', filters.reconciled.toString());
    if (filters?.search && filters.search.trim()) params.append('search', filters.search.trim());
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.pageSize) params.append('pageSize', String(filters.pageSize));

    const response = await apiService.get<GeneralLedgerResponse>(`/accounting/general-ledger?${params.toString()}`);
    return {
      data: response.data || [],
      total: response.total || 0,
      totalDebit: Number(response.totalDebit || 0),
      totalCredit: Number(response.totalCredit || 0),
      page: response.page || 1,
      pageSize: response.pageSize || 500,
      pages: response.pages || 0
    };
  }

  /**
   * Calculate account balance from posted entries
   */
  async getAccountBalanceFromEntries(accountId: string, upToDate?: Date | string): Promise<number> {
    const params = new URLSearchParams();
    if (upToDate) params.append('upToDate', new Date(upToDate).toISOString());

    const response = await apiService.get<{ data: { balance: number } }>(`/accounting/accounts/${accountId}/balance?${params.toString()}`);
    return response.data.balance || 0;
  }

  // ============ VALIDATION HELPERS ============

  /**
   * Validate double-entry (debit must equal credit)
   */
  validateDoubleEntry(lines: JournalEntryLine[]): { valid: boolean; message?: string } {
    if (lines.length < 2) {
      return { valid: false, message: 'Journal entry must have at least 2 lines' };
    }

    const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
    const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);

    const difference = Math.abs(totalDebit - totalCredit);
    if (difference > 0.01) {
      return {
        valid: false,
        message: `Debit ($${totalDebit.toFixed(2)}) must equal Credit ($${totalCredit.toFixed(2)}). Difference: $${difference.toFixed(2)}`
      };
    }

    // Check that each line has either debit or credit, but not both
    for (const line of lines) {
      const debit = Number(line.debit);
      const credit = Number(line.credit);

      if (debit > 0 && credit > 0) {
        return {
          valid: false,
          message: 'Each line must have either debit or credit, not both'
        };
      }
      if (debit === 0 && credit === 0) {
        return {
          valid: false,
          message: 'Each line must have either debit or credit amount'
        };
      }
    }

    return { valid: true };
  }

  /**
   * Calculate totals for journal entry lines
   */
  calculateTotals(lines: JournalEntryLine[]): { totalDebit: number; totalCredit: number } {
    const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
    const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);
    return { totalDebit, totalCredit };
  }

  // ============ BULK UPLOAD ENDPOINTS ============

  /**
   * Bulk upload journal entries from Excel file
   * Note: Uses fetch directly to handle FormData properly
   */
  async bulkUploadJournalEntries(file: File): Promise<BulkUploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');
    const baseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

    const response = await fetch(`${baseURL}/accounting/journal-entries/bulk-upload`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload file');
    }

    return data as BulkUploadResult;
  }
}

export const journalService = new JournalService();
export default journalService;